import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, formatApiError } from '@/App';
import { Plus, MagnifyingGlass, User, Envelope, Phone, IdentificationBadge, Trash, PencilSimple } from '@phosphor-icons/react';

const AdminProxies = () => {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [newProxy, setNewProxy] = useState({
    first_name: '',
    surname: '',
    id_number: '',
    personal_email: '',
    personal_phone: ''
  });

  useEffect(() => {
    fetchProxies();
  }, [search]);

  const fetchProxies = async () => {
    try {
      const params = search ? `?search=${search}` : '';
      const { data } = await api.get(`/proxies${params}`);
      setProxies(data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = Object.fromEntries(
        Object.entries(newProxy).filter(([, v]) => v !== '' && v !== null && v !== undefined)
      );
      await api.post('/proxies', payload);
      toast.success('Contact created');
      setIsDialogOpen(false);
      setNewProxy({
        first_name: '',
        surname: '',
        id_number: '',
        personal_email: '',
        personal_phone: ''
      });
      fetchProxies();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/proxies/${deleteTarget.proxy_id}`);
      toast.success(`${deleteTarget.first_name} ${deleteTarget.surname} deleted`);
      setDeleteTarget(null);
      fetchProxies();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSavingEdit(true);
    try {
      const payload = {
        first_name: editTarget.first_name,
        surname: editTarget.surname,
        id_number: editTarget.id_number || null,
        personal_email: editTarget.personal_email,
        personal_phone: editTarget.personal_phone || null,
      };
      await api.put(`/proxies/${editTarget.proxy_id}`, payload);
      toast.success('Contact updated');
      setEditTarget(null);
      fetchProxies();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <AdminLayout title="Contacts">
      <div className="space-y-6 fade-in">
        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4B5563]" />
                <Input
                  placeholder="Search contacts..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 rounded-none"
                  data-testid="search-input"
                />
              </div>

              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="create-proxy-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Contact
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle>Add New Contact</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-semibold">First Name *</Label>
                        <Input
                          value={newProxy.first_name}
                          onChange={(e) => setNewProxy({ ...newProxy, first_name: e.target.value })}
                          required
                          className="rounded-none mt-1"
                          data-testid="first-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-semibold">Surname *</Label>
                        <Input
                          value={newProxy.surname}
                          onChange={(e) => setNewProxy({ ...newProxy, surname: e.target.value })}
                          required
                          className="rounded-none mt-1"
                          data-testid="surname-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">ID Number (POPIA)</Label>
                      <Input
                        value={newProxy.id_number}
                        onChange={(e) => setNewProxy({ ...newProxy, id_number: e.target.value })}
                        className="rounded-none mt-1 font-mono"
                        placeholder="For compliance purposes"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Email *</Label>
                      <Input
                        type="email"
                        value={newProxy.personal_email}
                        onChange={(e) => setNewProxy({ ...newProxy, personal_email: e.target.value })}
                        required
                        className="rounded-none mt-1"
                        data-testid="email-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Phone</Label>
                      <Input
                        value={newProxy.personal_phone}
                        onChange={(e) => setNewProxy({ ...newProxy, personal_phone: e.target.value })}
                        className="rounded-none mt-1"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="rounded-none">
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none" data-testid="submit-proxy-btn">
                        Create Contact
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Contacts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : proxies.length > 0 ? (
            proxies.map((proxy) => (
              <Card key={proxy.proxy_id} className="card-hover relative group" data-testid={`proxy-card-${proxy.proxy_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#0055FF] rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {proxy.first_name?.[0]}{proxy.surname?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#0A192F]">{proxy.first_name} {proxy.surname}</h3>
                      <Badge className="mt-1">{proxy.verification_status}</Badge>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-[#0055FF] hover:bg-blue-50 rounded-none opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setEditTarget({ ...proxy })}
                      data-testid={`edit-proxy-btn-${proxy.proxy_id}`}
                      aria-label={`Edit ${proxy.first_name} ${proxy.surname}`}
                    >
                      <PencilSimple className="w-4 h-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-none opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setDeleteTarget(proxy)}
                      data-testid={`delete-proxy-btn-${proxy.proxy_id}`}
                      aria-label={`Delete ${proxy.first_name} ${proxy.surname}`}
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                      <Envelope className="w-4 h-4" />
                      <span className="truncate">{proxy.personal_email}</span>
                    </div>
                    {proxy.personal_phone && (
                      <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                        <Phone className="w-4 h-4" />
                        <span>{proxy.personal_phone}</span>
                      </div>
                    )}
                    {proxy.id_number && (
                      <div className="flex items-center gap-2 text-sm text-[#4B5563]">
                        <IdentificationBadge className="w-4 h-4" />
                        <span className="font-mono">****{proxy.id_number.slice(-4)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <User className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
              <p className="text-[#4B5563]">No contacts found</p>
              <Button 
                onClick={() => setIsDialogOpen(true)} 
                className="mt-4 bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
              >
                <Plus className="w-4 h-4 mr-2" /> Add First Contact
              </Button>
            </div>
          )}
        </div>

        <Dialog open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
          <DialogContent className="sm:max-w-[500px]" data-testid="edit-proxy-dialog">
            <DialogHeader>
              <DialogTitle>Edit Contact</DialogTitle>
            </DialogHeader>
            {editTarget && (
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold">First Name *</Label>
                    <Input
                      value={editTarget.first_name || ''}
                      onChange={(e) => setEditTarget({ ...editTarget, first_name: e.target.value })}
                      required
                      className="rounded-none mt-1"
                      data-testid="edit-first-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">Surname *</Label>
                    <Input
                      value={editTarget.surname || ''}
                      onChange={(e) => setEditTarget({ ...editTarget, surname: e.target.value })}
                      required
                      className="rounded-none mt-1"
                      data-testid="edit-surname-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold">ID Number (POPIA)</Label>
                  <Input
                    value={editTarget.id_number || ''}
                    onChange={(e) => setEditTarget({ ...editTarget, id_number: e.target.value })}
                    className="rounded-none mt-1 font-mono"
                    placeholder="13-digit SA ID"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Email *</Label>
                  <Input
                    type="email"
                    value={editTarget.personal_email || ''}
                    onChange={(e) => setEditTarget({ ...editTarget, personal_email: e.target.value })}
                    required
                    className="rounded-none mt-1"
                    data-testid="edit-email-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Phone</Label>
                  <Input
                    value={editTarget.personal_phone || ''}
                    onChange={(e) => setEditTarget({ ...editTarget, personal_phone: e.target.value })}
                    className="rounded-none mt-1"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setEditTarget(null)} className="rounded-none">
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={savingEdit}
                    className="bg-[#0055FF] hover:bg-[#0044CC] rounded-none"
                    data-testid="save-edit-proxy-btn"
                  >
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-proxy-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Delete {deleteTarget?.first_name} {deleteTarget?.surname}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this contact and remove all links to associated companies.
                Linked companies will remain intact. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} data-testid="cancel-delete-proxy">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-proxy"
              >
                {deleting ? 'Deleting...' : 'Delete Permanently'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProxies;
